// src/Pages/UKK/UKK.jsx
import React, { useLayoutEffect, useRef, useState, useEffect } from "react";
import { Helmet } from "@dr.pogodin/react-helmet";
import { useNavigate } from "react-router-dom";
import { motion } from "../../lib/motionSafe"; // polku suhteessa komponenttiin
import { HelpCircle, ChevronDown, Phone } from "lucide-react";
import NeonButton from "../../components/ResponsiveButton/NeonButton.jsx";
import { useTheme } from "../../context/ThemeContext";
import VideoHero from "../../components/VideoHero/VideoHero.jsx";
import heroVideo from "../../assets/ukk.mp4";
import poster from "../../assets/ukk-hero-poster.webp";

/* Vakaa, saavutettava akordeoni – ei "height:auto" animaatiota */
function FAQItem({ i, title, children, isOpen, onToggle }) {
  const { theme } = useTheme();
  const panelId = `faq-panel-${i}`;
  const btnId = `faq-button-${i}`;

  const contentRef = useRef(null);
  const [contentH, setContentH] = useState(0);

  useLayoutEffect(() => {
    if (contentRef.current) {
      setContentH(contentRef.current.scrollHeight);
    }
  }, [children, isOpen, title]);

  useEffect(() => {
    const onResize = () => {
      if (contentRef.current) setContentH(contentRef.current.scrollHeight);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const headerBase =
    theme === "dark"
      ? "bg-[#1a1630]/70 hover:bg-[#221b44]/80"
      : "bg-lemon/70 hover:bg-lemon/80";

  const containerRing = isOpen ? "ring-primary/40" : "ring-white/10";
  const containerBg = theme === "dark" ? "bg-white/5" : "bg-white/70";
  const bodyText = theme === "dark" ? "text-white/80" : "text-black/80";
  const titleText = theme === "dark" ? "text-white/90" : "text-black/90";
  // const iconText = theme === "dark" ? "text-white/85" : "text-black/85";

  return (
      <div className={`rounded-xl overflow-hidden backdrop-blur-md ring-1 ${containerRing} ${containerBg}`}>
         {/* Otsikkorivi */}
         <button
           id={btnId}
           aria-controls={panelId}
           aria-expanded={isOpen}
           onClick={onToggle}
           className={`group w-full flex items-center justify-between gap-4 px-4 sm:px-5 py-4 text-left ${headerBase}`}
         >
           {/* MUUTOS: lisätty min-w-0 vanhemmalle ja rivittymisluokat otsikolle */}
           <span className="flex items-center gap-3 min-w-0">{/* min-w-0 antaa lapsen kutistua flexissä */}
             <HelpCircle className="h-5 w-5 dark:text-primary drop-shadow" aria-hidden="true" />
             <span
               className={
                 `flex-1 min-w-0 break-words whitespace-normal ` + // ← MUUTOS: varmistaa rivittymisen
                 `font-customFont2 dark:text-white/80 text-black/80 ` +
                 `text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm ${titleText}`
               }
             >
               {title}
             </span>
           </span>
   
           <motion.span
             animate={{ rotate: isOpen ? 180 : 0 }}
             transition={{ type: "spring", stiffness: 260, damping: 22 }}
             className="inline-flex shrink-0 rounded-md ring-1 ring-white/10 bg-white/10 p-1"
           >
             <ChevronDown className="h-4 w-4" aria-hidden="true" />
           </motion.span>
         </button>
   
         {/* Sisältö – animoidaan numeeriseen korkeuteen (ei height:auto) */}
         <motion.div
           id={panelId}
           role="region"
           aria-labelledby={btnId}
           initial={false}
           animate={{ height: isOpen ? contentH : 0, opacity: isOpen ? 1 : 0 }}
           transition={{ duration: 0.28, ease: "easeInOut" }}
           style={{ overflow: "hidden" }}
         >
           <div ref={contentRef} className="px-4 sm:px-5 pb-4 pt-1">
             <div className={`dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm leading-relaxed ${bodyText}`}>{children}</div>
           </div>
         </motion.div>
       </div>
  );
}

// JSON-LD: FAQPage tilaajille
const faqTilaajatJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Miten Buukkeri toimii?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Etusivulla näet automaattisesti kuluvan päivän vapaat esiintyjät. Voit rajata hakua esimerkiksi esiintyjätyypin, musiikkityylin, maakunnan tai esiintyjän kotipaikan mukaan, avata profiilin ja tehdä tarjouspyynnön valitsemalla vihreän (vapaan) päivän kalenterista."
      }
    },
    {
      "@type": "Question",
      "name": "Miten tarjouspyyntö etenee?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Kun lähetät tarjouspyynnön, siitä lähtee ilmoitus sekä Buukkerille että esiintyjälle. Saat sähköpostiisi kokonaishinnan sekä linkin tarjouksen hyväksymistä tai perumista varten. Esiintyjän kalenterissa pyyntö näkyy tilassa odottaa ja tarjous on voimassa yhden tunnin; hyväksyessäsi linkistä varaus vahvistuu automaattisesti."
      }
    },
    {
      "@type": "Question",
      "name": "Miten maksan esiintymisen?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Maksu tapahtuu kahdessa vaiheessa. Varausta vahvistaessasi Buukkeri laskuttaa varausmaksun, joka hyvitetään lopullisesta hinnasta, mutta jota ei palauteta, jos peruutus tulee asiakkaan puolelta. Loppumaksusta sovitaan suoraan esiintyjän kanssa; haluttaessa Buukkeri voi hoitaa loppulaskutuksen erikseen sovittavalla tavalla."
      }
    },
    {
      "@type": "Question",
      "name": "Miten annan palautetta esiintymisestä?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Esiintymisen jälkeen saat sähköpostin, jossa on palautelinkki ja PIN-koodi. Linkin kautta annat tähtiarvion ja lyhyen kommentin. Antamasi tähdet lasketaan esiintyjän profiilissa näkyvään keskiarvoon ja yksi palaute voidaan antaa per varaus."
      }
    },
    {
      "@type": "Question",
      "name": "Onko Buukkerissa näkyvillä lista kaikista esiintyjistä?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Erillistä listaa kaikista esiintyjistä ei ole. Haussa näkyvät esiintyjät, jotka täyttävät kalenterin ja puskuriasetusten ehdot, esimerkiksi kun esiintyjällä on puskuri nollassa ja kyseinen päivä avattuna vihreänä kalenterissa."
      }
    },
    {
      "@type": "Question",
      "name": "Jos perun esiintymisen, miten maksujen kanssa käy?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Peruutukset tehdään aina kirjallisesti. Peruutusmaksu määräytyy kokonaishinnasta ja peruutusajankohdasta esiintymispäivään nähden. Varausmaksu on palauttamaton, ja se vähennetään mahdollisesta peruutusmaksusta; jos peruutusmaksu on varausmaksua suurempi, laskutetaan erotus, ja jos se on varausmaksua pienempi tai yhtä suuri, lisämaksuja ei tule."
      }
    }
  ]
};

export default function UKK() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState(0);
  const toggle = (idx) => setOpenIndex((prev) => (prev === idx ? -1 : idx));

  const heroOverlay =
    theme === "dark"
      ? "from-black/60 via-black/35 to-black/60"
      : "from-white/0 via-white/5 to-white/30";

  // ⚠️ TÄRKEÄÄ: faqs on ARRAY, ei objekti – .map toimii!
  // Tähän sivulle vain TILAAJALLE tarkoitetut kysymykset.
  const faqs = [
    // 1) Miten Buukkeri toimii?

 {
  title: "Miten Buukkeri toimii?",
  content: (
    <>
      <p>
        Näet etusivulla automaattisesti
        <strong> kuluvan päivän vapaat esiintyjät</strong>.
      </p>

      <p>
        Näet hakutulokset heti. Voit rajata listaa suodattimilla, kuten
        <strong> esiintyjätyyppi</strong>, <strong>musiikkityyli</strong>, <strong>maakunta</strong> tai
        <strong> esiintyjän kotipaikka</strong>. Voit myös selata kortteja vapaasti ja avata esiintyjän profiilin.
        Jos poistat hakuehdot, palaat näkymään, jossa näkyvät
        <strong> tämän päivän vapaat esiintyjät kaikista esiintyjätyypeistä</strong>.
      </p>

      <br />

      <p>
        Esiintyjän profiilissa valitse ensin kalenterista vapaa päivä (vihreä). Sen jälkeen valitse
        <strong>esiintymisen kesto</strong> ja täytä <strong>tapahtumapaikka</strong>.
        Lisää myös <strong>nimesi</strong> ja <strong>puhelinnumerosi</strong>.
        Lopuksi lähetä <strong>tarjouspyyntö</strong> — esiintymisen kesto vaikuttaa tarjottavaan hintaan.
      </p>

    </>
  ),
},

{
  title: "Miten tarjouspyyntö etenee?",
  content: (
    <>
      <h4 className="font-semibold">Miten tarjouspyyntö etenee?</h4>
      <p>
        Ilmoitus tarjouspyynöstä lähtee samanaikaisesti Buukkerille ja esiintyjälle. Sinä saat sähköpostiisi
        <strong> kokonaishinnan</strong> sekä linkin hyväksyntää tai perumista varten. Esiintyjän kalenterissa pyyntö
        näkyy tilassa ”odottaa”, kunnes hyväksyt, perut tai sen aika umpeutuu. Tarjous on voimassa <strong> yhden tunnin</strong>. Kun hyväksyt tarjouksen linkistä, buukkaus vahvistuu automaattisesti.
      </p>
    </>
  ),
},


{
  title: "Entä jos esiintyjä peruu?",
  content: (
    <>
      <p>
        Peruminen on harvinaista, mutta ilmoitamme heti, jos niin käy. Etsimme ensisijaisesti vastaavan korvaavan esiintyjän samaan hintaluokkaan ja lähetämme vaihtoehdot hyväksyttäväksesi.
      </p>
      <br />
      <p>
        Jos sopivaa korvaajaa ei löydy tai ehdot eivät sovi, tarjouspyyntö tai varaus perutaan ja päivä vapautuu. Et sitoudu mihinkään ilman omaa hyväksyntääsi.
      </p>
    </>
  ),
},

    {
  title: "Miten maksan esiintymisen?",
  content: (
    <>
      <p>
        Maksaminen tapahtuu kahdessa vaiheessa. Kun vahvistat varauksen,
        <strong> Buukkeri laskuttaa varausmaksun heti</strong>. Varausmaksu
        <strong> hyvitetään</strong> lopullisesta hinnasta, eikä sitä palauteta,
        jos sopimus päättyy asiakkaan peruutukseen. Varausta vahvistaessasi
        <strong> sitoudut Buukkerin peruutusehtoihin</strong>, jotka löydät
        sivulta <a href="/kayttoehdot#peruutukset" className="underline">/kayttoehdot</a>.
        Tarjouksen mukana saat ohjeet varausmaksun maksamiseen.
      </p>

      <br />

      <p>
        <strong>Loppumaksusta</strong> sovit suoraan esiintyjän kanssa. Jos
        esiintyjä haluaa, että loppumaksu laskutetaan
        <strong> Buukkerin kautta</strong>, ole yhteydessä Buukkeriin, niin
        järjestämme laskun (tilisiirto) ja ohjeet. Saat aina
        kirjallisen laskun tai kuitin; mahdollinen arvonlisävero eritellään.
      </p>

      <br />

      <h4 className="font-semibold">Tarvitsetko lisätietoja?</h4>
      <p>
        Jos et löytänyt vastausta kysymykseesi, ota rohkeasti yhteyttä. Autamme mielellämme:&nbsp;
        <a href="mailto:info@buukkeri.fi" className="underline">info@buukkeri.fi</a> tai puh.&nbsp;
        <a href="tel:+358443519265" className="underline">044 351 9265</a>.
      </p>
    </>
  ),
},

{
  title: "Miten annan palautetta esiintymisestä?",
  content: (
    <>
      <p>
        Saat <strong>esiintymisen jälkeen</strong> sähköpostin, joka sisältää
        <strong> palautelinkin</strong> ja <strong>PIN-koodin</strong>. Linkistä
        pääset suoraan lomakkeelle, jossa annat tähtiarvion ja lyhyen kommentin.
      </p>

      <ul className="list-disc pl-5 mt-2 space-y-1">
        <li>Palaute avautuu keikkapäivänä ja on aikarajoitettu (ikkuna kerrotaan sähköpostissa).</li>
        <li>PIN on myös varausvahvistuksessa — jos palautesähköposti hukkui, voit käyttää sitä.</li>
        <li>Jos viestiä ei näy, tarkista roskapostikansio.</li>
      </ul>

      <h4 className="mt-4 font-semibold">Miten tähdet vaikuttavat esiintyjän profiiliin?</h4>
      <ul className="list-disc pl-5 mt-2 space-y-1">
        <li>Antamasi tähtimäärä (1–5) lasketaan mukaan esiintyjän profiilissa näkyvään <strong>keskiarvoon</strong>.</li>
        <li>Keskiarvo päivittyy pian palautteen tallennuksen jälkeen.</li>
        <li>Voit antaa <strong>yhden palautteen per varaus</strong>.</li>
        <li>Palautteet auttavat muita tilaajia valitsemaan sopivan esiintyjän ja parantavat profiilin näkyvyyttä.</li>
      </ul>

    </>
  ),
},

{
  title: "Onko Buukkerissa näkyvillä lista siitä, mitä esiintyjiä palvelussa on?",
  content: (
    <>
      <p>
        Ei ole, <strong>ellei kaikilla esiintyjillä</strong> ole samaan aikaan
        <strong> puskuri nollassa</strong> ja <strong>sama päivä avattuna (vihreä)</strong> kalenterissa.
      </p>
    </>
  ),
},


{
  title: "Jos perun esiintymisen, miten maksujen kanssa käy?",
  content: (

  <>
    <p>
      Peruutukset tehdään aina <strong>kirjallisesti</strong> (esim. sähköpostitse).
      Peruutusmaksu määräytyy <strong>kokonaishinnasta</strong> ja aikarajat
      lasketaan esiintymispäivästä. Varausmaksu on <strong>palauttamaton</strong>.
    </p>

    <p className="mt-2 font-semibold">Peruutusmaksu (yleinen)</p>
    <ul className="list-disc pl-5 space-y-1">
      <li><strong>Jos peruminen tapahtuu ≥ 120 päivää ennen:</strong> ei maksua</li>
      <li><strong>Jos peruminen tapahtuu 30–119 päivää ennen:</strong> 25&nbsp;%</li>
      <li><strong>&lt; 30 päivää ennen:</strong> 100&nbsp;%</li>
    </ul>

    <p className="mt-3 font-semibold">Kesäsesonki (kesä–elokuu)</p>
    <ul className="list-disc pl-5 space-y-1">
      <li><strong>Jos peruminen tapahtuu ≥ 120 päivää ennen:</strong> ei maksua</li>
      <li><strong>Jos peruminen tapahtuu 91–119 päivää ennen:</strong> 25&nbsp;%</li>
      <li><strong>≤ 90 päivää ennen:</strong> 100&nbsp;%</li>
    </ul>

    <p className="mt-3 font-semibold">Maksu- ja palautuskäytäntö</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>Peruutusmaksusta <strong>vähennetään</strong> maksettu varausmaksu.</li>
        <li>Jos peruutusmaksu on varausmaksua <strong>suurempi</strong>, laskutamme erotuksen.</li>
        <li>Jos peruutusmaksu on varausmaksua <strong>pienempi tai yhtä suuri</strong>, lisämaksuja ei synny.</li>
        <li>Poikkeavat ehdot voidaan sopia erikseen; ne vahvistetaan kirjallisesti tarjouksessa/tilausvahvistuksessa.</li>
      </ul>
  </>
  ),
}

    // Jos haluat tilaajalle myös peruutus-infoa, lisää tänne oma tilaajalle kirjoitettu versio.
  ];

  return (
    <>
      <Helmet>
        <title>Usein kysyttyä – Buukkeri</title>
        <meta
          name="description"
          content="Usein kysytyt kysymykset esiintyjän varaamisesta ja Buukkerin käytöstä tilaajille."
        />
        <script type="application/ld+json">
          {JSON.stringify(faqTilaajatJsonLd)}
        </script>
      </Helmet>

      {/* HERO */}
    <VideoHero
        mp4Src={heroVideo}
        poster={poster}
        className="h-[50vh] min-h-[260px] mt-20"
        overlayClass={`bg-gradient-to-b ${heroOverlay}`}
        respectReducedMotion={false}
      >
        <motion.h1
                 className="font-customFont2 dark:text-shadow-lg text-center text-electric dark:text-primary text-5xl sm:text-6xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-6xl font-bold leading-tight"
                 initial={{ y: 12, opacity: 0 }}
                 animate={{ y: 0, opacity: 1 }}
                 transition={{ duration: 0.5 }}
               >
          Usein kysyttyä
        </motion.h1>
         <motion.p
                  className="mt-3 dark:text-white/80 text-white/80 font-customFont1 text-lg text-center text-shadow-lg"
                  initial={{ y: 12, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.05 }}
                >
          Vastauksia tilaajille Buukkerin käytöstä ja varauksista.
        </motion.p>
      </VideoHero>

      {/* Lista */}
      <main className="relative w-full pb-16">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="mt-8 grid gap-3">
            {faqs.map((f, idx) => (
              <motion.div
                key={idx}
                initial={{ y: 10, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true, margin: "-10% 0px -10% 0px" }}
                transition={{ duration: 0.3, ease: "easeOut", delay: idx * 0.03 }}
              >
                <FAQItem
                  i={idx}
                  title={f.title}
                  isOpen={openIndex === idx}
                  onToggle={() => toggle(idx)}
                >
                  {f.content}
                </FAQItem>
              </motion.div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-8 flex flex-col items-center gap-4">
            <NeonButton
              onClick={() => navigate("/otayhteytta")}
              variant="primary"
              size="sm"
              IconLeft={Phone}
            >
              Ota yhteyttä
            </NeonButton>
            <p className="dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm">
              Tarvitsetko vastauksen, jota et löytänyt? Autamme mielellämme.
            </p>
          </div>
        </div>
      </main>
    </>
  );
}
